import { useEffect, useMemo, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import SearchBar from '@/components/feature/SearchBar';
import MovieCard from '@/components/base/MovieCard';
import { CATEGORIES, type MovieCategory } from '@/types/movie';
import { getPopularMovies, searchMovies } from '@/utils/movieQuery';
import { formatDurationShort, formatRating } from '@/utils/format';
import { useDocumentTitle } from '@/hooks/useDocumentTitle';

export default function Search() {
  const [searchParams, setSearchParams] = useSearchParams();
  const keyword = searchParams.get('q') || '';
  const [category, setCategory] = useState<MovieCategory | '全部'>('全部');

  useEffect(() => {
    setCategory('全部');
  }, [keyword]);

  useDocumentTitle({
    title: keyword ? `“${keyword}”的搜索结果 - 午夜小电影` : '搜索影片 - 午夜小电影',
    description: `在午夜小电影搜索${keyword ? `“${keyword}”` : '午夜主题影片'}，支持按标题、简介与标签模糊检索，覆盖剧情、悬疑、恐怖等类型。`,
    keywords: '午夜小电影搜索,午夜影片检索,悬疑电影,恐怖电影',
  });

  const results = useMemo(() => searchMovies(keyword, category), [keyword, category]);
  const popular = getPopularMovies(6);

  const typeOptions: (MovieCategory | '全部')[] = ['全部', ...CATEGORIES.map((c) => c.key)];

  return (
    <div className="mx-auto w-full px-4 py-8 md:px-8 md:py-12">
      <nav className="flex flex-wrap items-center gap-2 text-xs text-foreground-500">
        <Link to="/" className="transition hover:text-primary-400">首页</Link>
        <i className="ri-arrow-right-s-line"></i>
        <span className="text-foreground-800">搜索</span>
      </nav>

      <header className="mt-6">
        <h1 className="text-2xl text-foreground-950 md:text-3xl">搜索午夜影片</h1>
        <p className="mt-2 text-sm text-foreground-600">输入关键词，检索影片标题、简介与标签。</p>
        <div className="mt-5 max-w-2xl">
          <SearchBar size="lg" defaultValue={keyword} />
        </div>
      </header>

      <div className="mt-6 flex flex-wrap items-center gap-2">
        <span className="text-xs text-foreground-500">类型筛选：</span>
        {typeOptions.map((opt) => (
          <button
            key={opt}
            type="button"
            onClick={() => setCategory(opt)}
            className={`whitespace-nowrap rounded-full px-3.5 py-1.5 text-xs transition ${
              category === opt
                ? 'bg-primary-500 text-white'
                : 'bg-background-200/70 text-foreground-600 hover:bg-background-300/70 hover:text-foreground-950'
            }`}
          >
            {opt}
          </button>
        ))}
      </div>

      <div className="mt-6 border-t border-background-300/40 pt-6">
        {keyword ? (
          <p className="text-sm text-foreground-600">
            关键词「<span className="text-foreground-950">{keyword}</span>」共找到{' '}
            <span className="font-display text-lg text-accent-500">{results.length}</span> 部影片
          </p>
        ) : (
          <p className="text-sm text-foreground-600">
            全部午夜影片共{' '}
            <span className="font-display text-lg text-accent-500">{results.length}</span> 部，输入关键词可进一步缩小范围。
          </p>
        )}
      </div>

      {results.length > 0 ? (
        <div className="mt-6 flex flex-col gap-4">
          {results.map((movie) => (
            <Link
              key={movie.id}
              to={`/detail/${movie.id}`}
              className="group flex gap-4 rounded-lg border border-background-300/50 bg-background-100 p-4 transition hover:border-primary-400/60"
            >
              <div className="relative h-32 w-24 shrink-0 overflow-hidden rounded-md bg-background-200">
                <img
                  src={movie.poster}
                  alt={`${movie.title} 海报`}
                  title={`${movie.title} 午夜小电影`}
                  className="h-full w-full object-cover object-top"
                />
              </div>
              <div className="min-w-0 flex-1">
                <div className="flex flex-wrap items-center gap-2">
                  <span className="rounded-md bg-secondary-100/80 px-2 py-0.5 text-[11px] text-secondary-900">
                    {movie.category}
                  </span>
                  <span className="flex items-center gap-1 text-[11px] text-accent-600">
                    <i className="ri-star-fill"></i>
                    {formatRating(movie.rating)}
                  </span>
                </div>
                <h2 className="mt-2 truncate text-base text-foreground-950 transition group-hover:text-primary-400">
                  {movie.title}
                </h2>
                <p className="mt-1 text-xs text-foreground-500">
                  {movie.year} · {formatDurationShort(movie.duration)} · 导演 {movie.director}
                </p>
                <p className="mt-2 line-clamp-2 text-sm leading-relaxed text-foreground-600">{movie.synopsis}</p>
                <div className="mt-2 hidden flex-wrap gap-1.5 sm:flex">
                  {movie.tags.map((tag) => (
                    <span key={tag} className="rounded-full bg-background-200/70 px-2 py-0.5 text-[11px] text-foreground-500">
                      {tag}
                    </span>
                  ))}
                </div>
              </div>
              <i className="ri-arrow-right-s-line hidden self-center text-xl text-foreground-500 transition group-hover:text-primary-400 sm:block"></i>
            </Link>
          ))}
        </div>
      ) : (
        <div className="mt-10 flex flex-col items-center justify-center rounded-lg border border-background-300/50 bg-background-100 py-16 text-center">
          <span className="flex h-16 w-16 items-center justify-center rounded-full bg-background-200/70 text-foreground-500">
            <i className="ri-search-2-line text-3xl"></i>
          </span>
          <h2 className="mt-5 text-lg text-foreground-950">未找到相关影片</h2>
          <p className="mt-2 max-w-md text-sm text-foreground-600">
            换个关键词试试，或从下面这些本月热门午夜影片里挑选一部。
          </p>
        </div>
      )}

      {results.length === 0 && (
        <section className="mt-12">
          <div className="mb-6 flex items-center gap-2">
            <span className="h-5 w-1 rounded-full bg-primary-500"></span>
            <h2 className="text-xl text-foreground-950">热门推荐</h2>
          </div>
          <div className="grid grid-cols-2 gap-4 md:grid-cols-3 md:gap-6 lg:grid-cols-6">
            {popular.map((movie) => (
              <MovieCard key={movie.id} movie={movie} />
            ))}
          </div>
        </section>
      )}
    </div>
  );
}